/**
 * The possible-duplicate signal (13.2-05, D-13.2-P-10): a hand-lodged incident and a later intake ticket for the same
 * event (the INC-11 / INC-12 shape). Oversight sees both records named; nothing acts on the signal. Closing one is
 * the audited `closeAsDuplicate` verb (`recordClosure.ts`), by a person.
 *
 * A pair matches only when all three hold:
 *   - **one hand-lodged, one intake:** exactly one of the two carries an `ExternalId` (the ShiftCare ticket). Two
 *     intake rows are two tickets, and the intake already dedupes on the ticket; two hand-lodged rows are not this
 *     signal's shape;
 *   - **the same client:** `RecipientClientId` equal when both carry one (two different ids are two clients, never a
 *     name match); otherwise any `RecipientName` in common, trimmed and case-folded, reading a multi-client intake
 *     list (`A; B`, `intake_build_lodge.js`) as its names;
 *   - **a near day:** the Melbourne day of `OccurredOn`, else `ReportedOn`, within `NEAR_DAYS` either way. An intake
 *     stub has no `OccurredOn`, so its ShiftCare report time stands in.
 *
 * `same-worker` adds weight when one row's `InjuredWorkerName` appears, as whole words, in the other's title,
 * description or injured worker. It never makes a match on its own.
 *
 * Never returned: the row itself; a cancelled candidate; a candidate already marked a duplicate (its
 * `DuplicateOfRef` set, so of this row or another). A row that is cancelled or already a duplicate gets no matches:
 * it has been settled.
 *
 * 13.2-10 adds what the record page needs around it: `matchWords` (the reasons in words, "same client, 2 days
 * apart"), `duplicateSignalApplies` (whether the page reads candidates at all) and `candidateWindow` (the one bounded
 * read's `ReportedOn` window).
 *
 * Pure: no Graph, no React, no clock (the caller passes `now`). Every day comes from the rows.
 */

import { liveChoice } from '../config/fieldset/live';
import type { Actor } from '../types/actionRegister';
import type { RecordRow } from '../types/records';
import { isOversight } from './actionAuthority';
import { melbourneDayOf, melbourneWallToInstant } from './fieldValues';
import { isBlank } from './recordVerbs';

/** The window, in Melbourne days, either way. */
export const NEAR_DAYS = 2;

export type DuplicateReason = 'same-client' | 'near-date' | 'same-worker';

export interface DuplicateMatch {
  /** The other record's ref. */
  ref: string;
  /** Always `same-client` and `near-date`; `same-worker` when the worker is named in both. In that order. */
  reasons: DuplicateReason[];
  /** Higher is likelier: client 2, the day 1–3 (3 = the same day), the worker 3. */
  score: number;
  /** Whole Melbourne days between the two rows' days (0 … `NEAR_DAYS`), for words like "2 days apart". */
  days: number;
}

const [S_CANCELLED] = liveChoice('incidents', 'Status', 'Cancelled');

const SCORE_CLIENT = 2;
const SCORE_WORKER = 3;
/** A worker name shorter than this is too likely to be part of another word or a common token. */
const WORKER_MIN = 3;

function text(value: unknown): string {
  return typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '';
}

/** Trimmed, inner spaces collapsed, case-folded. */
function fold(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase('en-AU');
}

function hasTicket(row: RecordRow): boolean {
  return !isBlank(row.fields.ExternalId);
}

/** The client names a row names (a multi-client intake list is `A; B`); `--` is the intake's "no name". */
function clientNames(row: RecordRow): Set<string> {
  return new Set(
    text(row.fields.RecipientName)
      .split(';')
      .map(fold)
      .filter((n) => n !== '' && n !== '--'),
  );
}

function sameClient(a: RecordRow, b: RecordRow): boolean {
  const idA = fold(text(a.fields.RecipientClientId));
  const idB = fold(text(b.fields.RecipientClientId));
  if (idA !== '' && idB !== '') return idA === idB;
  const names = clientNames(b);
  return [...clientNames(a)].some((n) => names.has(n));
}

/** The Melbourne day the row is about: `OccurredOn`, else `ReportedOn`. */
function dayOf(row: RecordRow): string | null {
  const occurred = row.fields.OccurredOn;
  const value = isBlank(occurred) ? row.fields.ReportedOn : occurred;
  return typeof value === 'string' ? melbourneDayOf(value) : null;
}

function daysApart(a: string, b: string): number {
  return Math.abs(Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000));
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Does `name` appear in `haystack` as whole words (not inside a longer word)? Both case-folded. */
function namedIn(name: string, haystack: string): boolean {
  if (name.length < WORKER_MIN || haystack === '') return false;
  const words = name.split(' ').map(escapeRegExp).join('\\s+');
  return new RegExp(`(^|[^\\p{L}\\p{N}])${words}($|[^\\p{L}\\p{N}])`, 'u').test(haystack);
}

/** One row's injured worker, named in the other's title, description or injured worker (either way round). */
function sameWorker(a: RecordRow, b: RecordRow): boolean {
  const said = (r: RecordRow) => fold([r.fields.Title, r.fields.Description, r.fields.InjuredWorkerName].map(text).join(' \n '));
  const workerA = fold(text(a.fields.InjuredWorkerName));
  const workerB = fold(text(b.fields.InjuredWorkerName));
  return namedIn(workerA, said(b)) || namedIn(workerB, said(a));
}

/** Settled: cancelled, or already marked a duplicate of another record. */
function settled(row: RecordRow): boolean {
  return row.fields.Status === S_CANCELLED || !isBlank(row.fields.DuplicateOfRef);
}

/**
 * The incidents in `candidates` that may describe the same event as `row`, best first (score, then the nearer day,
 * then the lower id). `[]` for a row that is not an incident, or is settled.
 */
export function possibleDuplicates(row: RecordRow, candidates: readonly RecordRow[]): DuplicateMatch[] {
  if (row.register !== 'incidents' || settled(row)) return [];
  const day = dayOf(row);
  if (day === null) return [];
  const found: (DuplicateMatch & { id: number })[] = [];
  for (const other of candidates) {
    if (other.register !== 'incidents' || other.ref === row.ref || other.id === row.id || settled(other)) continue;
    if (hasTicket(row) === hasTicket(other)) continue;
    if (!sameClient(row, other)) continue;
    const otherDay = dayOf(other);
    if (otherDay === null) continue;
    const days = daysApart(day, otherDay);
    if (days > NEAR_DAYS) continue;
    const reasons: DuplicateReason[] = ['same-client', 'near-date'];
    let score = SCORE_CLIENT + (NEAR_DAYS + 1 - days);
    if (sameWorker(row, other)) {
      reasons.push('same-worker');
      score += SCORE_WORKER;
    }
    found.push({ ref: other.ref, reasons, score, days, id: other.id });
  }
  return found
    .sort((a, b) => b.score - a.score || a.days - b.days || a.id - b.id)
    .map(({ ref, reasons, score, days }) => ({ ref, reasons, score, days }));
}

// ---------------------------------------------------------------------------
// 13.2-10: the record page's signal (Oversight only; never acts on its own)
// ---------------------------------------------------------------------------

/** "N days apart" in words: the same day, 1 day apart, 2 days apart. */
function apartWords(days: number): string {
  if (days === 0) return 'the same day';
  return days === 1 ? '1 day apart' : `${days} days apart`;
}

/**
 * A match's reasons in plain words, for Oversight: "same client, 2 days apart", "same client, the same day, the same
 * worker named". The day words use `DuplicateMatch.days`.
 */
export function matchWords(match: DuplicateMatch): string {
  const words: string[] = [];
  for (const reason of match.reasons) {
    if (reason === 'same-client') words.push('same client');
    else if (reason === 'near-date') words.push(apartWords(match.days));
    else words.push('the same worker named');
  }
  return words.join(', ');
}

/** A record that has or could have a twin: it holds a ShiftCare ticket, or it was made within this many days. */
export const TWIN_WINDOW_DAYS = 14;

/** The bounded candidate read: incidents whose `ReportedOn` is within this many Melbourne days of the row's day. */
export const CANDIDATE_WINDOW_DAYS = 7;

const DAY_MS = 86_400_000;

/**
 * Whether the record page reads candidates for `row` (13.2-10's measured gate). The read is one extra Graph call per
 * record open, so it is made only for Oversight (the signal is theirs alone), on an incident that is not settled and
 * has a day, and that has or could have a twin: it holds a ShiftCare ticket (`ExternalId`), or it was made within
 * `TWIN_WINDOW_DAYS` of `now` (`Created`). A practice record passes the same gate (its candidates are local).
 */
export function duplicateSignalApplies(row: RecordRow, actor: Actor, now: Date): boolean {
  if (row.register !== 'incidents' || !isOversight(actor) || settled(row) || dayOf(row) === null) return false;
  if (hasTicket(row)) return true;
  const created = Date.parse(text(row.fields.Created));
  return Number.isFinite(created) && now.getTime() - created <= TWIN_WINDOW_DAYS * DAY_MS && created - now.getTime() <= DAY_MS;
}

/** `YYYY-MM-DD` plus `n` days (calendar days, no clock). */
function shiftDay(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/**
 * The candidate read's window, as instants for a `ReportedOn` filter: from the start of the Melbourne day
 * `CANDIDATE_WINDOW_DAYS` before the row's day (`OccurredOn`, else `ReportedOn`) to the start of the day after the
 * same span after it (exclusive). `null` when the row has no day. A twin within `NEAR_DAYS` always falls inside: an
 * intake twin's day is its `ReportedOn`, and a hand-lodged twin is reported on or after its own day.
 */
export function candidateWindow(row: RecordRow): { from: string; to: string } | null {
  const day = dayOf(row);
  if (day === null) return null;
  const from = melbourneWallToInstant(shiftDay(day, -CANDIDATE_WINDOW_DAYS), '00:00');
  const to = melbourneWallToInstant(shiftDay(day, CANDIDATE_WINDOW_DAYS + 1), '00:00');
  return from.ok && to.ok ? { from: from.value, to: to.value } : null;
}
